import { action, computed, observable, makeObservable } from 'mobx';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { CallMemberStatus, ICallMemberDevice } from '@gem/call/Model/callModel.types';

import { Call } from '../Call';
import { Opponent } from '../Opponent/Opponent';
import { Member } from './Member';

interface IMemberDeviceConstructorParamsActive {
  call: Call;
  device: ICallMemberDevice;
  state: CallMemberStatus;
  isHandRaised: boolean;
  handRaisedAt: number | null;
  member: Member;
  isDefault: false;
}

interface IMemberDeviceConstructorParamsDefault {
  call: Call;
  device: null;
  state: null;
  isHandRaised: false;
  handRaisedAt: null;
  member: Member;
  isDefault: true;
}

type IMemberDeviceConstructorParams =
  | IMemberDeviceConstructorParamsActive
  | IMemberDeviceConstructorParamsDefault;

export class MemberDevice {
  call: Call;
  member: Member;
  uid: ProviderUid | null = null;
  memberId: string;
  deviceId: ProviderUid | null = null;
  shareScreenUid: ProviderUid | null = null;
  isDefault: boolean;
  isNewMember = false;
  @observable _state = CallMemberStatus.NotOnCall;
  @observable _isHandRaised = false;
  @observable _handRaisedAt: number | null = null;

  constructor(data: IMemberDeviceConstructorParams) {
    makeObservable(this);

    if (data.isDefault) {
      this.call = data.call;
      this.member = data.member;
      this.memberId = data.member.userId;
    } else {
      this.call = data.call;
      this.uid = data.device.providerUserUid;
      this.shareScreenUid = data.device.shareScreenUid;
      this.deviceId = data.device.deviceId;
      this._isHandRaised = data.isHandRaised;
      this._handRaisedAt = data.handRaisedAt;
      this.memberId = data.member.userId;
      this._state = data.state;
      this.member = data.member;
    }

    this.isDefault = data.isDefault;
  }

  @computed
  get isMyDevice(): boolean {
    return this.call.meStore.deviceId === this.deviceId;
  }

  @computed
  get opponent(): Opponent | null {
    if (!this.uid) {
      return null;
    }
    return this.call.opponents.getOpponent(this.uid) || null;
  }

  @computed
  get screenSharingOpponent(): Opponent | null {
    if (!this.shareScreenUid) {
      return null;
    }
    return this.call.opponents.getOpponent(this.shareScreenUid);
  }

  @computed
  get hasScreenSharingOpponent(): boolean {
    return Boolean(this.screenSharingOpponent);
  }

  @action
  setState(state: CallMemberStatus): void {
    this._state = state;
  }

  @action
  setHandRaised(value: boolean, ts: number | null = null): void {
    this._isHandRaised = value;
    this._handRaisedAt = ts;
  }

  @computed
  get state(): CallMemberStatus {
    if (this.isMyDevice) {
      return CallMemberStatus.OnCall;
    }
    if (this.opponent) {
      return CallMemberStatus.OnCall;
    }

    return this._state;
  }

  @computed
  get providerState(): CallMemberStatus {
    return this._state;
  }

  @computed
  get isOnCallByProvider(): boolean {
    return this.providerState === CallMemberStatus.OnCall;
  }

  @computed
  get isOnCall(): boolean {
    return this.state === CallMemberStatus.OnCall;
  }

  @computed
  get isAudioMuted(): boolean {
    if (this.isMyDevice) {
      return this.call.meStore.isAudioMuted;
    }
    if (!this.isOnCall) {
      return false;
    }
    return !this.opponent || !this.opponent.hasAudio;
  }

  @computed
  get isHandRaised(): boolean {
    if (this.isMyDevice) {
      return this.call.meStore.isHandRaised;
    }

    return this._isHandRaised;
  }

  @computed
  get handRaisedAt(): number | null {
    if (this.isMyDevice) {
      return this.call.meStore.handRaisedAt;
    }

    return this._handRaisedAt;
  }

  @computed
  get isMuting(): boolean {
    if (this.isMyDevice || !this.isOnCall || this.isAudioMuted) {
      return false;
    }
    if (this.call.isCallOwnerOrAdmin || this.call.isMeInitiator) {
      return true;
    }
    return false;
  }

  @computed
  get isRemoving(): boolean {
    if (this.isMyDevice || !this.isOnCall) {
      return false;
    }
    if (this.call.isCallOwnerOrAdmin || this.call.isMeInitiator) {
      return !this.member.isAdmin;
    }
    return false;
  }

  @computed
  get isActiveSpeaker(): boolean {
    if (this.isMyDevice) {
      return !this.call.meStore.isAudioMuted && this.call.meStore.isSpeaking;
    }
    return !!this.opponent && this.opponent.isActiveSpeaker;
  }
}
